import { useHistory } from "react-router-dom";
import { History } from 'history';
import { EventNotificationService } from "./event-notification";
import { useMemo } from "react";
import { firstOrDefault, remove, replaceFirstWhere } from "../utils/collection";

export interface INavigationObjectionOptions {
  title?: string;
  warning?: string;
  proceedPrompt?: string;
  cancelPrompt?: string;
  bypassObjectionPredicate?: (destinationUrl: string) => boolean;
}

export interface INavigationObjectionProps extends INavigationObjectionOptions {
  objector: string;
  proceedNavigation: Function;
  cancelNavigation: Function;
}

export enum NavigationServiceEvents {
  NavigationDecisionRequested = 'NavigationService.NavigationDecisionRequested',
}

const defaultObjection = {
  title: 'Unsaved Changes Detected',
  warning: 'You can proceed with leaving this page to discard your changes, or cancel to save your work and/or continue editing',
  proceedPrompt: 'Leave page',
  cancelPrompt: 'Cancel',
};

const noop = () => {};

let objections: INavigationObjectionProps[] = [];

const rescindAllObjections = () => {
  objections = [];
};

const getNavigationDecision = (destinationUrl: string) => {
  const props = firstOrDefault(objections, obj => !obj.bypassObjectionPredicate || obj.bypassObjectionPredicate(destinationUrl) === false);
  if(!props) { return Promise.resolve(); }

  const promise = new Promise<void>((resolve, reject) => {
    props.proceedNavigation = resolve;
    props.cancelNavigation = reject;
  });

  EventNotificationService.dispatch(NavigationServiceEvents.NavigationDecisionRequested, props);
  return promise;
};

class NavigationServiceClass {
  public setNavigationObjectionDefaults(title: string, warning: string, proceedPrompt: string, cancelPrompt: string) {
    defaultObjection.title = title;
    defaultObjection.warning = warning;
    defaultObjection.proceedPrompt = proceedPrompt;
    defaultObjection.cancelPrompt = cancelPrompt;
  }

  public registerObjection(objector: string, options?: INavigationObjectionOptions) {
    const props: INavigationObjectionProps = {
      objector,
      title: options?.title ?? defaultObjection.title,
      warning: options?.warning ?? defaultObjection.warning,
      proceedPrompt: options?.proceedPrompt ?? defaultObjection.proceedPrompt,
      cancelPrompt: options?.cancelPrompt ?? defaultObjection.cancelPrompt,
      bypassObjectionPredicate: options?.bypassObjectionPredicate,
      proceedNavigation: noop,
      cancelNavigation: noop,
    };

    objections = replaceFirstWhere(objections, p => p.objector === objector, props, true);
  }

  public rescindObjection(objector: string) {
    objections = remove(objections, o => o.objector === objector);
  }
}

export const NavigationService = new NavigationServiceClass();

class Navigator {
  constructor(private history: History<unknown>) {}

  public goTo(url: string) {
    getNavigationDecision(url)
    .then(() => {
      rescindAllObjections();
      this.history.push(url);
    })
    .catch();
  }
}

export const useNavigator = () => {
  const history = useHistory();
  const navigator = useMemo(() => new Navigator(history), [history]);

  return navigator;
};